import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { FilterProvider } from './context/FilterContext';
import Header from './components/Common/Header';
import Footer from './components/Common/Footer';
import UeberUns from './pages/UeberUns';
import HomePage from './pages/HomePage';
import ActivityDetail from './pages/ActivityDetail';
import ActivityControl from './pages/ActivityControl';
import MyActivities from './pages/MyActivities';
import AddActivity from './pages/AddActivityPage';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import Aufnahmeregeln from './pages/Aufnahmeregeln';
import ZeitangabenFormate from './pages/ZeitangabenFormate';
import EmailVerify from './pages/EmailVerify';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import MobileLoginPage from './pages/MobileLoginPage';
import KontoVervollstaendigen from './pages/KontoVervollstaendigen';
import DataDeletionStatus from './pages/DataDeletionStatus';
import CookieBanner from './components/Common/CookieBanner';
import InstallHint from './components/Common/InstallHint';
import SplashScreen from './components/Common/SplashScreen';
import WelcomeModal from './components/Common/WelcomeModal';
import ScrollToTop from './components/Common/ScrollToTop';
import { registerVisit } from './utils/visitCount';

// Layout-Komponente für Seiten mit Header/Footer
const LayoutWithHeaderFooter = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

function App() {
  // Startsequenz: Splash bis 2.0 s, danach der Cookie-Hinweis, zuletzt der
  // Installations-Hinweis. Jede Stufe meldet, wenn sie den Platz freigibt.
  const [splashDone, setSplashDone] = useState(false);
  const handleSplashFinished = useCallback(() => setSplashDone(true), []);

  const [cookieDone, setCookieDone] = useState(false);
  const handleCookieDismissed = useCallback(() => setCookieDone(true), []);

  // Besuchszähler: einmal pro Laden hochzählen, ausgewertet wird er anderswo.
  useEffect(() => {
    registerVisit();
  }, []);

  // Chrome/Android kündigt die Installierbarkeit über dieses Event an. Wir halten
  // es fest, statt den Dialog sofort zu zeigen – InstallHint löst ihn später aus.
  const [installPrompt, setInstallPrompt] = useState(null);
  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault(); // unterdrückt Chromes eigene Mini-Infobar
      setInstallPrompt(event);
    };
    const handleInstalled = () => setInstallPrompt(null);

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  return (
    <>
    {!splashDone && <SplashScreen onFinished={handleSplashFinished} />}
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <FilterProvider>
          <div className="app">
            <ScrollToTop />
            <WelcomeModal />
            {splashDone && <CookieBanner onDismissed={handleCookieDismissed} />}
            {splashDone && cookieDone && <InstallHint installPrompt={installPrompt} />}
            <Routes>
              {/* Home-Seite (Aktivitäten-Übersicht mit eigenem Header) */}
              <Route path="/" element={<LayoutWithHeaderFooter><HomePage /></LayoutWithHeaderFooter>} />

              {/* Aktivitäten-Seite (alias) */}
              <Route path="/aktivitaeten" element={<LayoutWithHeaderFooter><HomePage /></LayoutWithHeaderFooter>} />

              {/* "Über uns" mit Header/Footer */}
              <Route path="/ueber-uns" element={<LayoutWithHeaderFooter><UeberUns /></LayoutWithHeaderFooter>} />
              
              {/* Impressum und Datenschutz mit Header/Footer */}
              <Route path="/impressum" element={<LayoutWithHeaderFooter><Impressum /></LayoutWithHeaderFooter>} />
              <Route path="/datenschutz" element={<LayoutWithHeaderFooter><Datenschutz /></LayoutWithHeaderFooter>} />
              <Route path="/aufnahmeregeln" element={<LayoutWithHeaderFooter><Aufnahmeregeln /></LayoutWithHeaderFooter>} />
              <Route path="/zeitangaben-formate" element={<LayoutWithHeaderFooter><ZeitangabenFormate /></LayoutWithHeaderFooter>} />
              
              {/* Add Activity mit Header/Footer */}
              <Route path="/activities/new" element={<LayoutWithHeaderFooter><AddActivity /></LayoutWithHeaderFooter>} />
              
              {/* Alle anderen Routen mit Header/Footer */}
              <Route path="/activities/:id" element={<LayoutWithHeaderFooter><ActivityDetail /></LayoutWithHeaderFooter>} />
              <Route path="/aktivitaet-kontrolle/:id" element={<LayoutWithHeaderFooter><ActivityControl /></LayoutWithHeaderFooter>} />
              <Route path="/meine-aktivitaeten" element={<LayoutWithHeaderFooter><MyActivities /></LayoutWithHeaderFooter>} />
              <Route path="/email-bestaetigen" element={<EmailVerify />} />
              <Route path="/passwort-vergessen" element={<ForgotPassword />} />
              <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
              <Route path="/anmelden" element={<MobileLoginPage />} />
              <Route path="/konto-vervollstaendigen" element={<KontoVervollstaendigen />} />
              {/* Statusseite für Löschanfragen von Facebook (Data Deletion Callback) */}
              <Route path="/data-deletion-status" element={<DataDeletionStatus />} />
            </Routes>
          </div>
        </FilterProvider>
      </AuthProvider>
    </Router>
    </>
  );
}

export default App;
